/**
 * Wspólny typ i funkcje do sprawdzania pojedynczych opcji zamówienia
 * (order.options) - wydzielone z wheelbase-utils.ts, żeby mogły być
 * reużyte przez inną logikę opartą o te same opcje (patrz tow-hook.ts).
 *
 * @module order-options
 */

/** Reprezentuje pojedynczą opcję zamówienia */
export interface OrderOption {
  _id?: string;
  name?: string;
  checked?: boolean;
  /** Niektóre opcje (np. hak holowniczy) są oznaczane jako "required" zamiast/obok "checked" */
  required?: boolean;
  parentId?: string;
  [key: string]: unknown;
}

/**
 * Znajduje pierwszą opcję zamówienia spełniającą podany predykat.
 *
 * @param options - Tablica opcji zamówienia
 * @param predicate - Warunek dopasowania opcji
 * @returns Pasująca opcja lub undefined
 */
export function findOption(
  options: OrderOption[] | undefined | null,
  predicate: (option: OrderOption) => boolean,
): OrderOption | undefined {
  if (!Array.isArray(options)) return undefined;
  return options.find(predicate);
}

/**
 * Znajduje zaznaczoną (checked: true) opcję zamówienia o podanym parentId -
 * używane dla grup opcji (np. rozstaw osi, wymiary pojazdu), gdzie wiele
 * opcji dzieli ten sam parentId, a wybrana jest ta z checked: true.
 *
 * @param options - Tablica opcji zamówienia
 * @param parentId - Identyfikator nadrzędny szukanej opcji
 * @returns Pasująca opcja lub undefined
 */
export function findSelectedOptionByParentId(
  options: OrderOption[] | undefined | null,
  parentId: string,
): OrderOption | undefined {
  return findOption(
    options,
    (opt) => opt.checked === true && opt.parentId === parentId,
  );
}

/**
 * Czy opcja jest "aktywna" - zaznaczona (checked) albo oznaczona jako
 * wymagana (required). Niektóre pojedyncze opcje (np. hak holowniczy) nie
 * należą do żadnej grupy (brak parentId) i mogą być oznaczone jednym z tych
 * dwóch pól zależnie od tego, jak zostały wprowadzone w systemie zamówień.
 *
 * @param option - Opcja zamówienia (albo undefined, gdy nie znaleziono)
 */
export function isOrderOptionActive(option: OrderOption | undefined): boolean {
  return !!option && (option.checked === true || option.required === true);
}

/**
 * Czy w tablicy opcji zamówienia jest aktywna (checked lub required) opcja
 * o podanym `_id` - używane dla pojedynczych opcji identyfikowanych wprost
 * przez id, a nie przez parentId (patrz `isOrderOptionActive`).
 *
 * @param options - Tablica opcji zamówienia
 * @param id - `_id` szukanej opcji
 */
export function isOrderOptionActiveById(
  options: OrderOption[] | undefined | null,
  id: string,
): boolean {
  return isOrderOptionActive(findOption(options, (opt) => opt._id === id));
}
